"use client"

import { SYSTEM_STATUS_API_PATH } from "@src/constants/systemStatus"
import { logger } from "@src/utils/logger"
import { createContext, useContext, useEffect, useState } from "react"

export type SystemPostType = {
  id: string
  status: "maintenance" | "incident"
  message: string
}
export type SystemStatusType = Array<SystemPostType>

// Use undefined as sentinel to distinguish "not in provider" from "empty list"
const SystemStatusContext = createContext<SystemStatusType | undefined>(
  undefined
)

type ProviderProps = {
  children: React.ReactNode
}

export function SystemStatusProvider({ children }: ProviderProps) {
  const [systemStatus, setSystemStatus] = useState<SystemStatusType | null>(
    null
  )

  useEffect(() => {
    let cancelled = false

    async function loadSystemStatus() {
      try {
        const response = await fetch(SYSTEM_STATUS_API_PATH, {
          credentials: "same-origin",
        })
        if (!response.ok) {
          logger.warn("System status request returned a non-OK response", {
            context: "system-status-fetch",
            status: response.status,
          })
          return
        }
        const data = (await response.json()) as {
          systemStatus: SystemStatusType | null
        }
        if (!cancelled) {
          setSystemStatus(data.systemStatus)
        }
      } catch (error) {
        logger.warn("Failed to fetch system status", {
          context: "system-status-fetch",
          error,
        })
      }
    }

    void loadSystemStatus()

    return () => {
      cancelled = true
    }
  }, [])

  const statuses = getPrioritizedSystemStatuses(systemStatus)

  return (
    <SystemStatusContext.Provider value={statuses}>
      {children}
    </SystemStatusContext.Provider>
  )
}

export function useSystemStatus() {
  const context = useContext(SystemStatusContext)
  if (context === undefined) {
    throw new Error(
      "useSystemStatus must be used within a SystemStatusProvider"
    )
  }
  return context
}

// Prioritize incidents over maintenance; return all of the winning type
export function getPrioritizedSystemStatuses(
  systemStatuses: SystemStatusType | null
): SystemStatusType {
  if (!systemStatuses || systemStatuses.length === 0) return []

  const incidents = systemStatuses.filter(
    (status) => status.status === "incident"
  )
  if (incidents.length > 0) return incidents

  return systemStatuses.filter((status) => status.status === "maintenance")
}
